import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts'
import { apiClient } from '@/lib/api.ts'
import { StatTile, PeriodSelect, MONTH_OPTIONS } from './StatTile.tsx'

interface TrendPoint {
  month: string
  avgMarginPct: number
  totalGrossProfit: number
  totalRevenue: number
  invoiceCount: number
}

function fmtMonth(m: string) {
  const [y, mo] = m.split('-')
  const d = new Date(Number(y), Number(mo) - 1, 1)
  return d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' })
}

function fmtEur(v: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

interface Props {
  tenantId?: string | null
}

export function MargenTrendTile({ tenantId }: Props) {
  const [months, setMonths] = useState(12)

  const { data, isLoading, error } = useQuery<{ points: TrendPoint[] }>({
    queryKey: ['stats.margin-trend', months, tenantId],
    queryFn: () => {
      const params = new URLSearchParams({ months: String(months) })
      if (tenantId) params.set('tenantId', tenantId)
      return apiClient.get(`/stats/margin-trend?${params}`).then((r) => r.data)
    },
    staleTime: 5 * 60 * 1000,
  })

  const points = (data?.points ?? []).map((p) => ({
    ...p,
    label: fmtMonth(p.month),
  }))

  const avgAll = points.length > 0
    ? Math.round(points.reduce((s, p) => s + p.avgMarginPct, 0) / points.length * 10) / 10
    : null

  return (
    <StatTile
      title="Margen-Trend"
      subtitle="Durchschnittliche Marge % pro Monat (aus Rechnungen)"
      loading={isLoading}
      error={error as Error | null}
      empty={!isLoading && !error && points.length === 0}
      emptyMessage="Noch keine Rechnungsdaten synchronisiert."
      headerRight={
        <PeriodSelect value={months} options={MONTH_OPTIONS} onChange={setMonths} />
      }
    >
      {avgAll !== null && (
        <div className="flex items-center gap-3 mb-3">
          <div>
            <span className="text-h2 font-bold text-gray-900">{avgAll} %</span>
            <span className="text-caption text-gray-500 ml-2">Ø über Zeitraum</span>
          </div>
        </div>
      )}
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={points} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null
              const p = payload[0].payload as TrendPoint & { label: string }
              return (
                <div className="bg-white border border-gray-200 rounded shadow-sm px-3 py-2 text-small">
                  <div className="font-medium text-gray-900">{p.label}</div>
                  <div className="text-gray-600">Ø Marge: <span className="font-semibold">{p.avgMarginPct.toFixed(1)} %</span></div>
                  <div className="text-gray-600">Rohertrag: <span className="font-semibold">{fmtEur(p.totalGrossProfit)}</span></div>
                  <div className="text-gray-600">Umsatz: <span className="font-semibold">{fmtEur(p.totalRevenue)}</span></div>
                  <div className="text-gray-400">{p.invoiceCount} Rechnungen</div>
                </div>
              )
            }}
          />
          <ReferenceLine y={10} stroke="#f59e0b" strokeDasharray="3 3" />
          <Bar dataKey="avgMarginPct" radius={[3, 3, 0, 0]} maxBarSize={40}>
            {points.map((p, i) => (
              <Cell
                key={i}
                fill={p.avgMarginPct >= 10 ? '#22c55e' : p.avgMarginPct > 0 ? '#f59e0b' : '#ef4444'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p className="text-caption text-gray-400 mt-1">
        Gestrichelte Linie = 10 % Zielmarke
      </p>
    </StatTile>
  )
}
